import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import downloadAndApplyFontFamilyOperation from 'editor/src/store/design/operation/downloadAndApplyFontFamilyOperation';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import CollapsableContainer from 'editor/src/component/CollapsableContainer';

import UploadedFontItem from './UploadedFontItem';

import styles from './index.module.scss';

const INITIAL_VISIBLE_COUNT = 5;

interface Props {
  currentFontFile: string | undefined;
}

function UploadedFontsSection({ currentFontFile }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const uploadedFonts = useSelector((state) => state.fonts.uploadedFonts);
  const [isExpanded, setIsExpanded] = useState(false);

  const visibleFonts = useMemo(() => {
    if (isExpanded || uploadedFonts.length <= INITIAL_VISIBLE_COUNT) {
      return uploadedFonts;
    }
    return uploadedFonts.slice(0, INITIAL_VISIBLE_COUNT);
  }, [uploadedFonts, isExpanded]);

  const hiddenCount = uploadedFonts.length - INITIAL_VISIBLE_COUNT;

  const onSelect = useCallback(
    (fontFile: string) => {
      void dispatch(downloadAndApplyFontFamilyOperation(fontFile));
    },
    [dispatch],
  );

  // TODO: Re-enable when delete API is ready (Phase 2)
  // const onDelete = useCallback(
  //   (fontFile: string) => {
  //     dispatch(
  //       openPopupOperation(PopupName.LOCAL_CONFIRMATION_POPUP, {
  //         popupTitle: t('txt_editor_font_delete_title'),
  //         textLines: [t('txt_editor_font_delete_confirm')],
  //         hideTitleIcon: true,
  //         options: [
  //           { title: t('txt_editor_font_delete_cancel') },
  //           {
  //             title: t('txt_editor_font_delete_action'),
  //             onConfirm: () => {
  //               sendPostMessage('fonts.deleteRequested', { fontFile });
  //             },
  //           },
  //         ],
  //       }),
  //     );
  //   },
  //   [dispatch, t],
  // );

  if (uploadedFonts.length === 0) {
    return null;
  }

  return (
    <div className={styles.uploadedFontsSection}>
      <CollapsableContainer
        className={styles.collapsableContainer}
        headerClassName={styles.collapsableHeader}
        title={t('txt_editor_font_uploaded_section')}
        defaultExpand
      >
        {visibleFonts.map((font) => (
          <UploadedFontItem
            key={font.fontFile}
            font={font}
            isActive={font.fontFile === currentFontFile}
            onSelect={onSelect}
          />
        ))}
        {hiddenCount > 0 && (
          <button type="button" className={styles.viewMoreButton} onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? t('txt_editor_font_view_less') : t('txt_editor_font_view_more', { count: hiddenCount })}
          </button>
        )}
      </CollapsableContainer>
    </div>
  );
}

export default React.memo(UploadedFontsSection);
